// src/features/streams/components/simulcast/YouTubeDestinationFlow.tsx

import { useState } from 'react';
import {
  Stack,
  Button,
  Text,
  Alert,
  TextInput,
  Group,
  Loader,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { YouTubeLogo } from './PlatformLogos';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BroadcastMetadataOverrideSection } from './BroadcastMetadataOverrideSection';
import { useQueryClient } from '@tanstack/react-query';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { EXTPLAT_QUERY_KEYS, YOUTUBE_LIVE_SCOPES } from '@/lib/constants/extplat';
import { SIMULCAST_CONSTRAINTS } from '@/lib/constants/simulcast';
import {
  ExtPlatAuthUtil,
  type ISimulcastAllData,
} from '@/lib/types/Simulcast';
import type { StreamViewData } from '@/lib/types/Stream';
import { ytCreateFormSchema, type YTCreateFormData } from '../../schemas/SimulcastForm.schema';
import { SimulcastTransforms } from '../../simulcast/simulcastTransforms';
import { useCreateSimulcastYT } from '../../useSimulcastMutations';
import { ExtPlatConfig } from '@/shared/utils/extplatBrandConfig';
import { ExtPlatApi } from '@/shared/api/ExtPlatApi';

// --- Types ---

type FlowStage =
  | { stage: 'connect' }
  | { stage: 'processing'; message: string }
  | { stage: 'configure'; extPlatAuthId: number }
  | { stage: 'error'; message: string };

interface YouTubeDestinationFlowProps {
  mode: 'create' | 'reconnect';
  stream: StreamViewData;
  simulcast?: ISimulcastAllData;
  onClose: () => void;
  onBack: () => void;
}

// --- Configure Form (create stage 2) ---

interface ConfigureFormProps {
  extPlatAuthId: number;
  stream: StreamViewData;
  onClose: () => void;
  onBack: () => void;
}

function ConfigureForm({ extPlatAuthId, stream, onClose, onBack }: ConfigureFormProps) {
  const createSimulcastYT = useCreateSimulcastYT(stream.media.mediaId);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<YTCreateFormData>({
    resolver: zodResolver(ytCreateFormSchema),
    defaultValues: {
      label: '',
      title: '',
      descShort: '',
    },
  });

  const { field: labelField } = useController({ name: 'label', control });

  const onFormSubmit = handleSubmit((data) => {
    const req = SimulcastTransforms.buildYTCreateReq(extPlatAuthId, data);
    createSimulcastYT.mutate(req, {
      onSuccess: () => {
        notifications.show({
          title: 'Destination Created',
          message: `"${data.label}" has been added.`,
          color: 'green',
        });
        onClose();
      },
      onError: (err) => {
        notifications.show({
          title: 'Error',
          message: err.message,
          color: 'red',
        });
      },
    });
  });

  return (
    <form onSubmit={onFormSubmit}>
      <Stack gap="md">
        <Alert color="green" icon={<IconCheck size={16} />}>
          YouTube account connected
        </Alert>

        <TextInput
          label="Destination Name"
          description="A name to identify this destination in your dashboard"
          placeholder="e.g., Production YouTube Channel"
          value={labelField.value}
          onChange={labelField.onChange}
          onBlur={labelField.onBlur}
          error={errors.label?.message}
          maxLength={SIMULCAST_CONSTRAINTS.LABEL_MAX}
          withAsterisk
        />

        <BroadcastMetadataOverrideSection
          streamTitle={stream.media.title}
          streamDescription={stream.media.descShort ?? ''}
          control={control}
          errors={errors}
        />

        <Group justify="space-between" mt="md">
          <Button variant="subtle" onClick={onBack} disabled={createSimulcastYT.isPending}>
            Back
          </Button>
          <Button type="submit" loading={createSimulcastYT.isPending}>
            Create
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

// --- Inner Flow Component (must be inside GoogleOAuthProvider) ---

function YouTubeFlowInner({
  mode,
  stream,
  simulcast,
  onClose,
  onBack,
}: YouTubeDestinationFlowProps) {
  const { selectedCId, selectedCustomer } = useAuth();
  const queryClient = useQueryClient();
  const ytClientId = ExtPlatConfig.getYouTubeClientId(selectedCustomer?.cfg.reseller);

  const [flowStage, setFlowStage] = useState<FlowStage>({ stage: 'connect' });

  const handleAuthSuccess = async (code: string, scope: string) => {
    setFlowStage({ stage: 'processing', message: 'Connecting to YouTube...' });
    try {
      const authReq = ExtPlatAuthUtil.createYTCreateReq(
        scope,
        'YouTube Connection',
        ytClientId,
        code,
      );

      if (mode === 'reconnect') {
        const extPlatAuthId = simulcast!.core.extPlatAuthId!;
        await ExtPlatApi.updateExtPlatAuth(selectedCId!, extPlatAuthId, authReq);

        queryClient.invalidateQueries({
          queryKey: MEDIA_QUERY_KEYS.stream(selectedCId!, stream.media.mediaId),
        });
        queryClient.invalidateQueries({
          queryKey: EXTPLAT_QUERY_KEYS.extPlatAuths(selectedCId!),
        });

        notifications.show({
          title: 'Reconnected',
          message: 'YouTube account has been reconnected.',
          color: 'green',
        });
        onClose();
        return;
      }

      // Create flow: create new ExtPlatAuth
      const created = await ExtPlatApi.createExtPlatAuth(selectedCId!, authReq);

      queryClient.invalidateQueries({
        queryKey: EXTPLAT_QUERY_KEYS.extPlatAuths(selectedCId!),
      });

      setFlowStage({ stage: 'configure', extPlatAuthId: created.extPlatAuthId });
    } catch (err) {
      setFlowStage({
        stage: 'error',
        message: err instanceof Error ? err.message : 'Failed to connect to YouTube',
      });
    }
  };

  const login = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'popup',
    scope: YOUTUBE_LIVE_SCOPES.join(' '),
    onSuccess: (codeResponse) => {
      handleAuthSuccess(codeResponse.code, codeResponse.scope);
    },
    onError: (error) => {
      setFlowStage({
        stage: 'error',
        message: error.error_description || 'YouTube login failed',
      });
    },
    onNonOAuthError: () => {
      // User closed the popup or it was blocked — return to connect stage
      setFlowStage({ stage: 'connect' });
    },
  });

  const handleConnect = () => {
    login();
  };

  // Render configure form when in that stage
  if (flowStage.stage === 'configure') {
    return (
      <ConfigureForm
        extPlatAuthId={flowStage.extPlatAuthId}
        stream={stream}
        onClose={onClose}
        onBack={onBack}
      />
    );
  }

  // Connect / processing / error stages
  return (
    <Stack align="center" gap="md" py="xl">
      <YouTubeLogo size={64} />

      {flowStage.stage === 'connect' && (
        <>
          <Text ta="center" maw={350}>
            {mode === 'create'
              ? 'Connect your YouTube account to create a live broadcast.'
              : 'Reconnect your YouTube account to refresh your authentication.'}
          </Text>
          <Button size="md" color="red" onClick={handleConnect}>
            {mode === 'create' ? 'Connect with YouTube' : 'Reconnect with YouTube'}
          </Button>
        </>
      )}

      {flowStage.stage === 'processing' && (
        <>
          <Loader size="md" color="red" />
          <Text c="dimmed" size="sm">
            {flowStage.message}
          </Text>
        </>
      )}

      {flowStage.stage === 'error' && (
        <>
          <Alert color="red" w="100%">
            {flowStage.message}
          </Alert>
          <Button variant="light" onClick={() => setFlowStage({ stage: 'connect' })}>
            Try Again
          </Button>
        </>
      )}

      <Button variant="subtle" onClick={onBack} mt="md">
        Back
      </Button>
    </Stack>
  );
}

// --- Main Component (wraps inner with GoogleOAuthProvider) ---

export function YouTubeDestinationFlow(props: YouTubeDestinationFlowProps) {
  const { selectedCustomer } = useAuth();
  const ytClientId = ExtPlatConfig.getYouTubeClientId(selectedCustomer?.cfg.reseller);

  return (
    <GoogleOAuthProvider clientId={ytClientId}>
      <YouTubeFlowInner {...props} />
    </GoogleOAuthProvider>
  );
}
